import React, { useEffect, useState } from 'react'
import {
  Alert, Badge, Box, Button, CircularProgress, Typography,
} from '@mui/material'
import CheckCircleIcon  from '@mui/icons-material/CheckCircleOutlined'
import CancelIcon       from '@mui/icons-material/CancelOutlined'
import HourglassIcon    from '@mui/icons-material/HourglassEmptyOutlined'
import { supabase } from '../../supabaseClient'
import { useViewerStudent } from '../../auth/ViewAsContext'

// Things waiting on the student's attention. Surfaces:
//   - Topic submission awaiting mentor review (from research_topic.status)
//   - Decided revision requests (approved / denied) until dismissed
// Designed to extend with more update sources (purchased equipment,
// scheduled meetings, etc.).

interface RevisionUpdate {
  kind:        'revision_decided'
  id:          string
  decision:    'approved' | 'denied'
  step_number: number
  reason:      string
  mentor_note: string | null
  decided_at:  string
}

interface SubmissionPendingUpdate {
  kind:           'submission_pending'
  id:             string
  step_number:    number
  submitted_at:   string
  finalist_count: number
}

interface MentorMessageUpdate {
  kind:        'mentor_message'
  id:          string
  arrived_at:  string
  excerpt:     string
}

type Update = RevisionUpdate | SubmissionPendingUpdate | MentorMessageUpdate

function timeSince(iso: string) {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function StudentUpdatesPanel() {
  const { studentId, viewerIsStudent } = useViewerStudent()
  const [updates,  setUpdates]  = useState<Update[]>([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState<string | null>(null)
  const [dismissing, setDismissing] = useState<string | null>(null)

  async function load() {
    if (!studentId) { setLoading(false); return }
    setLoading(true); setError(null)
    try {
      // Resolve the student's active project (needed for the topic-pending check).
      const { data: project } = await supabase
        .from('project')
        .select('project_id')
        .eq('student_id', studentId)
        .eq('status', 'active')
        .maybeSingle()
      const projectId = (project as { project_id: string } | null)?.project_id ?? null

      const [{ data: revs, error: revErr }, topicRes, rqRes, bgRes, hdRes] = await Promise.all([
        supabase
          .from('revision_request')
          .select('id, status, step_number, reason, mentor_note, decided_at')
          .eq('student_id', studentId)
          .in('status', ['approved', 'denied'])
          .is('acknowledged_at', null)
          .order('decided_at', { ascending: false }),
        projectId
          ? supabase
              .from('research_topic')
              .select('id, status, finalist_recommendation_ids, updated_at')
              .eq('project_id', projectId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        projectId
          ? supabase
              .from('research_question')
              .select('id, status, draft_payload, updated_at')
              .eq('project_id', projectId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        projectId
          ? supabase
              .from('background_research')
              .select('id, status, updated_at')
              .eq('project_id', projectId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        projectId
          ? supabase
              .from('hypothesis_design')
              .select('id, status, updated_at')
              .eq('project_id', projectId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ])
      if (revErr) throw new Error(revErr.message)

      const items: Update[] = (revs ?? []).map((r: any) => ({
        kind:        'revision_decided',
        id:          r.id,
        decision:    r.status,
        step_number: r.step_number,
        reason:      r.reason,
        mentor_note: r.mentor_note,
        decided_at:  r.decided_at,
      }))

      const topic = (topicRes.data as {
        id: string; status: string; finalist_recommendation_ids: string[] | null; updated_at: string;
      } | null)
      if (topic && topic.status === 'submitted') {
        items.unshift({
          kind:           'submission_pending',
          id:             topic.id,
          step_number:    1,
          submitted_at:   topic.updated_at,
          finalist_count: (topic.finalist_recommendation_ids ?? []).length,
        })
      }

      const rq = (rqRes.data as { id: string; status: string; updated_at: string } | null)
      if (rq && rq.status === 'submitted') {
        items.unshift({
          kind:           'submission_pending',
          id:             rq.id,
          step_number:    2,
          submitted_at:   rq.updated_at,
          finalist_count: 0,
        })
      }

      const bg = (bgRes.data as { id: string; status: string; updated_at: string } | null)
      if (bg && bg.status === 'submitted') {
        items.unshift({
          kind:           'submission_pending',
          id:             bg.id,
          step_number:    3,
          submitted_at:   bg.updated_at,
          finalist_count: 0,
        })
      }

      const hd = (hdRes.data as { id: string; status: string; updated_at: string } | null)
      if (hd && hd.status === 'submitted') {
        items.unshift({
          kind:           'submission_pending',
          id:             hd.id,
          step_number:    4,
          submitted_at:   hd.updated_at,
          finalist_count: 0,
        })
      }

      /* A message from staff, from either channel it can arrive on:
           mentor_review_log  a 'message' action attached to the project
           student_message    a direct send from the Send a Message page
         Both are "staff wrote to me and I have not answered". The dashboard
         only read the first, so a direct message reached the student's inbox
         and their mailbox but never their dashboard. */
      if (projectId) {
        const { data: latestMsg } = await supabase
          .from('mentor_review_log')
          .select('log_id, action, note, acted_at')
          .eq('project_id', projectId)
          .in('action', ['message', 'student_message'])
          .order('acted_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        const latest = latestMsg as { log_id: string; action: string; note: string | null; acted_at: string } | null
        if (latest && latest.action === 'message') {
          const text = (latest.note ?? '').trim()
          items.unshift({
            kind:       'mentor_message',
            id:         latest.log_id,
            arrived_at: latest.acted_at,
            excerpt:    text.slice(0, 140) + (text.length > 140 ? '…' : ''),
          })
        }
      }

      /* Unanswered direct messages: every staff_to_student message newer than
         the student's own last reply. Shown individually rather than as a
         count — a student needs to read them, not tally them. */
      const { data: thread } = await supabase
        .from('student_message')
        .select('id, direction, body, created_at')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false })
        .limit(20)
      const rows = (thread ?? []) as { id: string; direction: string; body: string; created_at: string }[]
      const lastStudentReplyAt = rows.find(r => r.direction === 'student_to_staff')?.created_at ?? null
      for (const r of rows) {
        if (r.direction !== 'staff_to_student') continue
        if (lastStudentReplyAt && r.created_at <= lastStudentReplyAt) continue
        const text = (r.body ?? '').trim()
        items.unshift({
          kind:       'mentor_message',
          id:         `sm-${r.id}`,
          arrived_at: r.created_at,
          excerpt:    text.slice(0, 200) + (text.length > 200 ? '…' : ''),
        })
      }

      setUpdates(items)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [studentId])

  async function dismiss(id: string) {
    setDismissing(id); setError(null)
    try {
      const { error: updErr } = await supabase
        .from('revision_request')
        .update({ acknowledged_at: new Date().toISOString() })
        .eq('id', id)
      if (updErr) throw new Error(updErr.message)
      setUpdates(prev => prev.filter(u => u.id !== id))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setDismissing(null)
    }
  }

  if (loading) {
    return (
      <Box sx={{ mb: 3, py: 1.5, textAlign: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    )
  }

  if (updates.length === 0) return null

  return (
    <Box sx={{
      mb: 3, p: 2.5, borderRadius: 2,
      border: '1px solid', borderColor: 'primary.light',
      bgcolor: 'rgba(15,43,76,0.025)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Updates for you</Typography>
        <Badge badgeContent={updates.length} color="primary" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {updates.map(u => {
          if (u.kind === 'mentor_message') {
            return (
              <Box
                key={u.id}
                sx={{
                  display:    'flex',
                  alignItems: 'flex-start',
                  gap:        1.5,
                  p:          1.5,
                  borderRadius: 1.5,
                  bgcolor:    'background.paper',
                  border:     '1px solid',
                  borderColor: 'primary.light',
                }}
              >
                <HourglassIcon sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }} fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    A message from your mentor
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {timeSince(u.arrived_at)}
                  </Typography>
                  {u.excerpt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55, fontStyle: 'italic' }}>
                      "{u.excerpt}"
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Open <strong>Messages</strong> in the menu to read it and reply.
                  </Typography>
                </Box>
              </Box>
            )
          }

          if (u.kind === 'submission_pending') {
            return (
              <Box
                key={u.id}
                sx={{
                  display:    'flex',
                  alignItems: 'flex-start',
                  gap:        1.5,
                  p:          1.5,
                  borderRadius: 1.5,
                  bgcolor:    'background.paper',
                  border:     '1px solid',
                  borderColor: 'warning.light',
                }}
              >
                <HourglassIcon sx={{ color: 'warning.main', mt: '2px', flexShrink: 0 }} fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Awaiting mentor review of your Step {u.step_number} submission
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Submitted {timeSince(u.submitted_at)}
                    {u.finalist_count > 0
                      ? ` · ${u.finalist_count} finalist${u.finalist_count !== 1 ? 's' : ''}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
                    Your mentor will review and either approve or send back with feedback.
                    You'll see their decision here.
                  </Typography>
                </Box>
              </Box>
            )
          }

          // revision_decided
          const approved   = u.decision === 'approved'
          const Icon       = approved ? CheckCircleIcon : CancelIcon
          const headlineColor = approved ? 'success.main' : 'error.main'

          return (
            <Box
              key={u.id}
              sx={{
                display:    'flex',
                alignItems: 'flex-start',
                gap:        1.5,
                p:          1.5,
                borderRadius: 1.5,
                bgcolor:    'background.paper',
                border:     '1px solid',
                borderColor: 'divider',
              }}
            >
              <Icon sx={{ color: headlineColor, mt: '2px', flexShrink: 0 }} fontSize="small" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {approved
                    ? `Mentor approved your Step ${u.step_number} revision request`
                    : `Mentor declined your Step ${u.step_number} revision request`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {timeSince(u.decided_at)}
                </Typography>
                {approved ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
                    Step 1 is open for revision — your categories, topics, and finalists are
                    still there. Edit in place: swap a finalist, pick a previously-considered
                    topic, or propose a new one. Your Step 2 work has been reset and will
                    rebuild from your new selections.
                  </Typography>
                ) : (
                  u.mentor_note && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      Note: {u.mentor_note}
                    </Typography>
                  )
                )}
                {approved && u.mentor_note && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                    Mentor's note: "{u.mentor_note}"
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={() => dismiss(u.id)}
                disabled={dismissing === u.id || !viewerIsStudent}
                sx={{ flexShrink: 0 }}
              >
                {dismissing === u.id ? <CircularProgress size={14} /> : 'Got it'}
              </Button>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
